import type { AxiosInstance, AxiosResponse } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from './envelope';

/**
 * The 7-method contract every entity service exposes.
 *
 * - `T` — the entity row shape (e.g. `Product`).
 * - `C` — the create payload (usually `Omit<T, ...timestamps + id>`).
 * - `F` — the filter payload, typically `Partial<Pick<T, ...>>`.
 *
 * Two wire protocols are supported via {@link createCrudService}:
 *   - `'rest'` — standard REST (api.ebillbook): GET list, POST one,
 *     PUT one, GET/DELETE /{id}. Default for new code.
 *   - `'legacy-action'` — old-style action-style RPC: POST /all, POST /Get,
 *     POST /Delete with envelope-wrapped responses. Kept for any service
 *     that hasn't migrated yet.
 *
 * Either way, callers see the same `Promise<T> / Promise<T[]> / Promise<void>`
 * — only the wire layer differs.
 */
export interface CrudService<T, C, F> {
  /** List every active row. */
  all(): Promise<T[]>;
  /** Fetch one row by id. */
  get(id: string): Promise<T>;
  /** Fetch the first row matching `filter`. */
  find(filter: F): Promise<T>;
  /** Fetch every row matching `filter`. */
  filter(filter: F): Promise<T[]>;
  /** Bulk insert; returns created rows. */
  create(items: C[]): Promise<T[]>;
  /** Bulk update; returns updated rows. */
  update(items: T[]): Promise<T[]>;
  /** Soft / hard delete. */
  delete(id: string): Promise<void>;
}

export type CrudProtocol = 'rest' | 'legacy-action';

/* -------------------------------------------------------------------- */
/*  REST helpers — defensive about list/object response shape           */
/* -------------------------------------------------------------------- */

function pickList<T>(res: AxiosResponse<unknown>): T[] {
  const d = res.data as unknown;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: T[]; data?: T[] | { items?: T[] } };
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.data)) return obj.data;
    if (obj.data && Array.isArray((obj.data as { items?: T[] }).items)) {
      return (obj.data as { items: T[] }).items;
    }
  }
  return [];
}

function pickOne<T>(res: AxiosResponse<unknown>): T {
  const d = res.data as unknown;
  if (d && typeof d === 'object') {
    const obj = d as { data?: T };
    if (obj.data && typeof obj.data === 'object') return obj.data as T;
  }
  return d as T;
}

/**
 * Convert a filter object into a query string. Skips null/undefined/empty.
 */
function toQuery(filter: Record<string, unknown> | undefined): string {
  if (!filter) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(filter)) {
    if (v == null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/* -------------------------------------------------------------------- */
/*  Factory                                                             */
/* -------------------------------------------------------------------- */

/**
 * Build a {@link CrudService} for an entity.
 *
 * @param client     Shared axios instance.
 * @param basePath   URL prefix, e.g. `'/master-data/Product'`. No trailing slash.
 * @param idKey      Primary-key field on the entity (camelCase). Used to
 *                   pick the id from a row in `update(items)` and to wrap
 *                   the id in legacy delete requests.
 * @param protocol   `'rest'` (default) or `'legacy-action'`.
 */
export function createCrudService<T, C, F>(
  client: AxiosInstance,
  basePath: string,
  idKey: string,
  protocol: CrudProtocol = 'rest',
): CrudService<T, C, F> {
  if (protocol === 'rest') {
    return {
      all: () => client.get(basePath).then((r) => pickList<T>(r)),

      get: (id: string) =>
        client.get(`${basePath}/${encodeURIComponent(id)}`).then((r) => pickOne<T>(r)),

      // The new API doesn't expose a dedicated `/find` — fall back to a
      // filtered list and grab the first result.
      find: (filter: F) =>
        client
          .get(`${basePath}${toQuery(filter as unknown as Record<string, unknown>)}`)
          .then((r) => {
            const items = pickList<T>(r);
            if (!items.length) throw new Error('Not found');
            return items[0]!;
          }),

      filter: (filter: F) =>
        client
          .get(`${basePath}${toQuery(filter as unknown as Record<string, unknown>)}`)
          .then((r) => pickList<T>(r)),

      create: async (items: C[]) => {
        // New API expects ONE entity per call. Fan out for bulk creates.
        const results: T[] = [];
        for (const item of items) {
          const r = await client.post(basePath, item);
          results.push(pickOne<T>(r));
        }
        return results;
      },

      update: async (items: T[]) => {
        const results: T[] = [];
        for (const item of items) {
          const r = await client.put(basePath, item);
          results.push(pickOne<T>(r));
        }
        return results;
      },

      delete: (id: string) =>
        client.delete(`${basePath}/${encodeURIComponent(id)}`).then(() => undefined),
    };
  }

  /* ----- legacy-action — preserve old POST /all, /Get, /Delete shape ----- */
  const wireKey = idKey.charAt(0).toUpperCase() + idKey.slice(1);
  return {
    all: () =>
      client
        .post<ObjectResponse<T[] | { items?: T[] | null }>>(`${basePath}/all`, {})
        .then((r) => unwrapItems<T>(r.data)),

    get: (id: string) =>
      client
        .post<ObjectResponse<T>>(`${basePath}/Get`, { [wireKey]: id })
        .then((r) => unwrap(r.data)),

    find: (filter: F) =>
      client
        .post<ObjectResponse<T>>(`${basePath}/find`, filter)
        .then((r) => unwrap(r.data)),

    filter: (filter: F) =>
      client
        .post<ObjectResponse<T[] | { items?: T[] | null }>>(`${basePath}/filter`, filter)
        .then((r) => unwrapItems<T>(r.data)),

    create: (items: C[]) =>
      client
        .post<ObjectResponse<T[] | { items?: T[] | null }>>(basePath, { items })
        .then((r) => unwrapItems<T>(r.data)),

    update: (items: T[]) =>
      client
        .put<ObjectResponse<T[] | { items?: T[] | null }>>(basePath, { items })
        .then((r) => unwrapItems<T>(r.data)),

    delete: (id: string) =>
      client
        .post<ObjectResponse<null>>(`${basePath}/Delete`, { [wireKey]: id })
        .then((r) => {
          unwrap(r.data, { allowNullData: true });
        }),
  };
}
